import React, { useState, useEffect } from 'react';
import { collection, addDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db, storage } from '../../utils/firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// Robust upload function using Firebase SDK
async function uploadTrailImage(file, folder = "trailImages") {
    try {
        // Sanitize & uniquify file name (spaces can be annoying in URLs)
        const safeName = `${Date.now()}-${file.name.replace(/\s+/g, "_")}`;
        const storageRef = ref(storage, `${folder}/${safeName}`);

        // Use uploadBytesResumable for better error handling and progress tracking
        const task = uploadBytesResumable(storageRef, file, { 
            contentType: file.type || 'image/jpeg' // Default to jpeg if type not detected
        });

        // Wait for upload completion
        await new Promise((resolve, reject) => {
            task.on("state_changed", 
                (snapshot) => {
                    // Progress tracking (optional - can be used for progress bars)
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    console.log(`Upload is ${progress}% done`);
                },
                (error) => {
                    console.error("Upload error:", error);
                    reject(error);
                },
                () => {
                    console.log("Upload completed successfully");
                    resolve();
                }
            );
        });

        // Get download URL
        const downloadURL = await getDownloadURL(storageRef);
        console.log('File uploaded successfully:', downloadURL);
        return downloadURL;
    } catch (error) {
        console.error('Error in uploadTrailImage:', error);
        throw error;
    }
}

const AdminTrailManager = () => {
    const [trails, setTrails] = useState([]);

    const [newTrail, setNewTrail] = useState({
        name: '',
        location: '',
        length: '',
        hours: '',
        difficulty: 'Easy',
        activity: 'hiking',
        description: '',
        linkmap: ''
    });

    const [trailFile, setTrailFile] = useState(null);
    const [mapFile, setMapFile] = useState(null);
    const [weekendDay, setWeekendDay] = useState('Saturday');

    useEffect(() => {
        fetchTrails();
    }, []);

    const fetchTrails = async () => {
        try {
            const trailsSnapshot = await getDocs(collection(db, 'trails'));
            const trailsData = trailsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setTrails(trailsData);
        } catch (error) {
            console.error("Error fetching trails:", error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewTrail(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTrailFileChange = (e) => {
        if (e.target.files[0]) {
            setTrailFile(e.target.files[0]);
        }
    };

    const handleMapFileChange = (e) => {
        if (e.target.files[0]) {
            setMapFile(e.target.files[0]);
        }
    };

    const calculateNextWeekendDate = (selectedDay) => {
        const now = new Date();
        const today = now.getDay();
        const targetDay = selectedDay === 'Sunday' ? 0 : 6;
        const diff = (targetDay + 7 - today) % 7 || 7;
        const result = new Date(now);
        result.setDate(now.getDate() + diff);
        result.setHours(10, 0, 0, 0);
        return result;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const startTime = calculateNextWeekendDate(weekendDay);
            const endTime = new Date(startTime);
            endTime.setHours(startTime.getHours() + 3);

            // Upload images using Firebase SDK (handles CORS automatically)
            let imageURL = '';
            let mapURL = '';
            
            try {
                // Upload trail image if provided
                if (trailFile) {
                    imageURL = await uploadTrailImage(trailFile, "trailImages");
                }
                
                // Upload map image if provided
                if (mapFile) {
                    mapURL = await uploadTrailImage(mapFile, "mapImages");
                }
            } catch (uploadError) {
                console.error('Error uploading images:', uploadError);
                // Show user-friendly error but continue with trail creation
                alert(`Warning: Could not upload images: ${uploadError.message}. Trail will be created without images.`);
                // Continue with empty URLs - trail can still be created
            }
            const trailData = {
                ...newTrail,
                hours: newTrail.hours, // ✅ make sure this is present
                start_time: startTime,
                end_time: endTime,
                image: imageURL,
                map: mapURL,
                weekend_day: weekendDay
              };
              

            await addDoc(collection(db, 'trails'), trailData);
            console.log('Trail created successfully in database');

            // Reset form
            setNewTrail({
                name: '',
                location: '',
                length: '',
                hours: '',
                difficulty: 'Easy',
                activity: 'hiking',
                description: '',
                linkmap: ''
            });
            setTrailFile(null);
            setMapFile(null);
            setWeekendDay('Saturday');
            
            // Refresh the trails list
            await fetchTrails();
            
            // Show success message
            alert('Trail created successfully!');
        } catch (error) {
            console.error("Error adding trail:", error);
            alert('Error creating trail: ' + error.message);
        }
    };

    const handleDelete = async (trailId) => {
        if (window.confirm('Are you sure you want to delete this trail?')) {
            try {
                await deleteDoc(doc(db, 'trails', trailId));
                fetchTrails();
            } catch (error) {
                console.error("Error deleting trail:", error);
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 p-8">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl font-bold mb-8">Trail Manager</h1>

                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <h2 className="text-xl font-semibold mb-4">Add New Trail</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input type="text" name="name" placeholder="Trail Name" value={newTrail.name} onChange={handleInputChange} className="border p-2 rounded" required />
                        <input type="text" name="location" placeholder="Location" value={newTrail.location} onChange={handleInputChange} className="border p-2 rounded" required />

                        <select value={weekendDay} onChange={(e) => setWeekendDay(e.target.value)} className="border p-2 rounded">
                            <option value="Saturday">Saturday</option>
                            <option value="Sunday">Sunday</option>
                        </select>

                        <input type="text" name="length" placeholder="Length (e.g., 10.1 km)" value={newTrail.length} onChange={handleInputChange} className="border p-2 rounded" required />

                        <input type="number" name="hours" placeholder="Est. Hours (e.g., 3)" value={newTrail.hours} onChange={handleInputChange} className="border p-2 rounded" required />

                        <select name="difficulty" value={newTrail.difficulty} onChange={handleInputChange} className="border p-2 rounded" required>
                            <option value="Easy">Easy</option>
                            <option value="Moderate">Moderate</option>
                            <option value="Challenging">Challenging</option>
                        </select>
                        <select name="activity" value={newTrail.activity} onChange={handleInputChange} className="border p-2 rounded" required>
                            <option value="hiking">Hiking</option>
                            <option value="running">Running</option>
                        </select>
                        <div>
                            <label className="block mb-1">Trail Picture</label>
                            <input type="file" accept="image/*" onChange={handleTrailFileChange} className="border p-2 rounded w-full" />
                        </div>
                        <div>
                            <label className="block mb-1">Map Picture</label>
                            <input type="file" accept="image/*" onChange={handleMapFileChange} className="border p-2 rounded w-full" />
                        </div>
                        <input type="text" name="linkmap" placeholder="Map Link" value={newTrail.linkmap} onChange={handleInputChange} className="border p-2 rounded" required />
                        <textarea name="description" placeholder="Description" value={newTrail.description} onChange={handleInputChange} className="border p-2 rounded md:col-span-2" required />
                    </div>
                    <button type="submit" className="mt-4 bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700">Add Trail</button>
                </form>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-4">Existing Trails</h2>
                    <div className="space-y-4">
                        {trails.map(trail => (
                            <div key={trail.id} className="border p-4 rounded flex justify-between items-center">
                                <div>
                                    <h3 className="font-semibold">{trail.name}</h3>
                                    <p className="text-sm text-gray-600">{trail.location}</p>
                                </div>
                                <button onClick={() => handleDelete(trail.id)} className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600">Delete</button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminTrailManager;
